import { z } from 'zod';
import { CandidateBatchResourceSchema } from './experiment.js';
/**
 * Video Candidates, as the browser reads them.
 *
 * The shape says the thing the domain says: a Video Candidate is an ordered sequence of
 * Scenes, and the rendered MP4 and the YouTube URL are deliveries hanging off it. A
 * Candidate with no delivery at all is complete; a Candidate whose URL was refused is
 * still exportable. Neither state is an error and neither is missing data.
 */
export const VIDEO_SCENES_PATH = '/api/v1/video-scenes';
export const VIDEO_DELIVERY_RESOURCE = 'videoDelivery';
/** The Studio's soft defaults when the Brief says nothing about timing. */
export const DEFAULT_VIDEO_SCENE_COUNT = 5;
export const DEFAULT_VIDEO_TOTAL_SECONDS = 20;
export const VideoOrientationSchema = z.enum(['portrait', 'landscape']);
export const VideoSceneRoleSchema = z.enum(['opening', 'development', 'proof', 'feature', 'closing']);
/**
 * What a Scene is built from. Any mixture, and none of them required - including real
 * product footage, which is why an empty list is a legal Scene rather than a defect.
 */
export const VideoSceneSourceSchema = z.enum([
    'generatedFootage',
    'productCapture',
    'animatedImagery',
    'graphics',
    'text',
]);
export const VideoSceneResourceSchema = z.object({
    id: z.string().min(1),
    candidateId: z.string().min(1),
    position: z.number().int().nonnegative(),
    role: VideoSceneRoleSchema,
    startMs: z.number().int().nonnegative(),
    durationMs: z.number().int().positive(),
    action: z.string(),
    /** Words on the frame. This is what carries the meaning when the sound is off. */
    onScreenCopy: z.string(),
    sources: z.array(VideoSceneSourceSchema),
    /** Present only when the Brief asked for voice. Captions exist exactly when it does. */
    speech: z.string().nullable(),
    sourceLibraryItemId: z.string().nullable(),
    artifactId: z.string().nullable(),
});
export const VideoDeliveryKindSchema = z.enum(['animatic', 'render']);
export const VideoUrlStatusSchema = z.enum(['absent', 'accepted', 'rejected']);
export const VideoUrlProblemResourceSchema = z.object({
    code: z.string(),
    expected: z.string(),
    observed: z.string(),
});
/**
 * What YouTube said about the video the address points at, which is not the same fact as
 * whether the address is well formed.
 *
 * `unchecked` covers both "nothing has been saved yet" and "the Studio could not reach
 * YouTube", with `urlReachNote` telling the two apart. It is deliberately not a third
 * kind of failure: a fact that could not be read must not harden into a refusal, and
 * none of the three values has ever stopped the MP4 being downloaded or exported.
 */
export const VideoUrlReachSchema = z.enum(['unchecked', 'playable', 'unplayable']);
/** Something the Studio describes and this deployment cannot perform, with its reason. */
export const StudioCapabilityResourceSchema = z.object({
    available: z.boolean(),
    note: z.string(),
});
/**
 * One delivery of a Video Candidate. File readiness and delivery readiness are separate
 * fields because they are separate facts: `artifactId` is an MP4 that exists, and
 * `urlStatus` is what Play would be handed. Neither implies the other.
 */
export const VideoDeliveryResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    candidateId: z.string().min(1),
    kind: VideoDeliveryKindSchema,
    state: z.enum(['working', 'ready', 'failed']),
    artifactId: z.string().nullable(),
    captionsArtifactId: z.string().nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
    youtubeUrl: z.string().nullable(),
    urlStatus: VideoUrlStatusSchema,
    urlProblems: z.array(VideoUrlProblemResourceSchema),
    /** What YouTube itself said. Independent of `urlStatus`, and never a blocker. */
    urlReach: VideoUrlReachSchema,
    urlReachNote: z.string().nullable(),
    urlCheckedAt: z.string().nullable(),
    message: z.string().nullable(),
    updatedAt: z.string().min(1),
});
/** A place Google's own material is silent or contradicts itself. Never a refusal. */
export const VideoVerificationResourceSchema = z.object({
    code: z.string(),
    detail: z.string(),
    source: z.string(),
});
export const VideoCandidateResourceSchema = z.object({
    candidateId: z.string().min(1),
    version: z.number().int().nonnegative(),
    experimentId: z.string().min(1),
    /** The App this belongs to, so the bench can offer that App's Library material. */
    appId: z.string().min(1),
    parentId: z.string().nullable(),
    headline: z.string(),
    summary: z.string(),
    refinement: z.string().nullable(),
    keep: z.boolean(),
    hidden: z.boolean(),
    orientation: VideoOrientationSchema,
    totalMs: z.number().int().positive(),
    /** Music and effects by default; voice only when the Brief asked for it. */
    music: z.boolean(),
    voice: z.boolean(),
    /** True exactly when speech exists, because that is when captions are owed. */
    captions: z.boolean(),
    /**
     * Whether the spoken line is actually spoken in the file.
     *
     * It is not: the Studio writes and times speech into the caption file and renders music
     * and effects only. The limitation travels with the Candidate rather than living in a
     * help page, because the alternative was a screen that said `voice over` about a track
     * that had never contained one.
     */
    voiceSynthesis: StudioCapabilityResourceSchema,
    scenes: z.array(VideoSceneResourceSchema),
    animatic: VideoDeliveryResourceSchema.nullable(),
    render: VideoDeliveryResourceSchema.nullable(),
    /** The short manual handoff, in the order it is done. */
    uploadChecklist: z.array(z.string()),
    verifications: z.array(VideoVerificationResourceSchema),
    rulesVersion: z.string(),
    verifiedAt: z.string(),
});
export const VideoShelfResourceSchema = z.object({
    experimentId: z.string().min(1),
    appId: z.string().min(1),
    orientation: VideoOrientationSchema,
    candidates: z.array(VideoCandidateResourceSchema),
});
/**
 * What one change to a Video Candidate came back as.
 *
 * Rewording a Scene, reordering the story, swapping in material that already exists and
 * recomposing for the other orientation all produce a complete descendant immediately -
 * there is nothing to buy. Asking for a Scene to be imagined again has to be generated,
 * so what comes back is the batch doing it. As with a slide edit, the discriminant is
 * in the body: one action, two prices.
 */
export const VideoEditResponseSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('descendant'), candidate: VideoCandidateResourceSchema }),
    z.object({ kind: z.literal('regenerating'), batch: CandidateBatchResourceSchema }),
]);
/**
 * Rewriting one Scene. Words, not settings: `instruction` asks for the Scene to be made
 * again, while the direct fields correct what is written on it. Naming material puts
 * bytes that already exist into the Scene with no provider call.
 */
export const EditSceneRequestSchema = z
    .object({
    instruction: z.string().optional(),
    onScreenCopy: z.string().optional(),
    action: z.string().optional(),
    speech: z.string().nullable().optional(),
    role: VideoSceneRoleSchema.optional(),
    sources: z.array(VideoSceneSourceSchema).optional(),
    materialIds: z.array(z.string().min(1)).optional(),
})
    .strict();
export const ReorderScenesRequestSchema = z.object({ sceneIds: z.array(z.string().min(1)).min(1) });
/** Absent orientation means "the other one", which is the only thing adapting can mean. */
export const AdaptOrientationRequestSchema = z.object({ orientation: VideoOrientationSchema.optional() });
/**
 * Render one, several, or all selected Video Candidates. Keep organises the shelf; it
 * has never been an eligibility gate, so the named Candidates are rendered whether or
 * not they are kept.
 */
export const RenderVideoRequestSchema = z.object({ candidateIds: z.array(z.string().min(1)).min(1) });
export const RenderVideoResponseSchema = z.object({
    renders: z.array(z.object({ candidateId: z.string().min(1), jobId: z.string().min(1) })),
});
/** Saving the URL the operator got back from YouTube. Empty text clears it again. */
export const SaveVideoDeliveryRequestSchema = z.object({ youtubeUrl: z.string() });
/**
 * The cover a Video Candidate is previewed against. It is a reading, never a write: the
 * feature graphic stays a separate Asset Type with its own Experiment, and previewing
 * here changes nothing about it.
 */
export const VideoCoverPreviewResourceSchema = z.object({
    experimentId: z.string().min(1),
    source: z.enum(['featureGraphic', 'libraryItem', 'none']),
    artifactId: z.string().nullable(),
    label: z.string(),
    note: z.string(),
    /** App Library material that could stand in temporarily, with bytes already stored. */
    options: z.array(z.object({ libraryItemId: z.string().min(1), artifactId: z.string().min(1), label: z.string() })),
});
export function videoShelfPath(experimentId) {
    return `/api/v1/experiments/${experimentId}/video`;
}
export function videoCandidatePath(candidateId) {
    return `/api/v1/candidates/${candidateId}/video`;
}
export function videoSceneOrderPath(candidateId) {
    return `/api/v1/candidates/${candidateId}/video-scenes/order`;
}
export function videoSceneEditPath(sceneId) {
    return `${VIDEO_SCENES_PATH}/${sceneId}/edit`;
}
export function adaptOrientationPath(candidateId) {
    return `/api/v1/candidates/${candidateId}/adapt-orientation`;
}
export function videoRendersPath(experimentId) {
    return `/api/v1/experiments/${experimentId}/video-renders`;
}
export function videoDeliveryPath(candidateId) {
    return `/api/v1/candidates/${candidateId}/video-delivery`;
}
export function videoCoverPreviewPath(experimentId, libraryItemId) {
    const base = `/api/v1/experiments/${experimentId}/video-cover-preview`;
    return libraryItemId === undefined ? base : `${base}?libraryItemId=${encodeURIComponent(libraryItemId)}`;
}
//# sourceMappingURL=video.js.map